<template>
  <div class="sospensioni-page">
    <div class="card">
      <div class="flex justify-content-between align-items-center mb-4">
        <h4 class="m-0">Sospensioni (SM1)</h4>
      </div>

      <!-- Sezione 1: Inserimento Manuale -->
      <div class="card mb-4 form-card">
        <div class="form-header cursor-pointer" @click="formManualeAperto = !formManualeAperto">
          <h5 class="m-0">
            <i class="pi pi-pencil mr-2" style="color: #FFC107;"></i>
            Caricamento Manuale Singolo Record
          </h5>
          <Button :icon="formManualeAperto ? 'pi pi-chevron-up' : 'pi pi-chevron-down'"
            class="p-button-text p-button-rounded p-button-sm" @click.stop="formManualeAperto = !formManualeAperto" />
        </div>

        <transition name="expand">
          <div v-show="formManualeAperto" class="form-content">
            <SospensioniForm v-model="formData" />

            <div class="flex justify-content-end mt-3">
              <Button label="Invia Record alla PDC" icon="pi pi-send" class="p-button-warning"
                @click="inviaRecordSingolo" :loading="loadingRecord" />
            </div>
          </div>
        </transition>
      </div>

      <!-- Sezione 2: Caricamento Massivo CSV -->
      <div class="card mb-4" style="background-color: #f8f9fa;">
        <h5 class="mb-3">
          <i class="pi pi-upload mr-2" style="color: #2196F3;"></i>
          Caricamento Massivo
        </h5>

        <FileUpload ref="fileUploader" name="csvFile" :multiple="false" accept=".csv" :maxFileSize="5000000"
          :customUpload="true" @uploader="handleFileUpload" :auto="false" chooseLabel="Seleziona CSV"
          uploadLabel="Invia alla PDC" cancelLabel="Annulla">
          <template #empty>
            <p>Trascina qui il file CSV o clicca per selezionarlo.</p>
          </template>
        </FileUpload>

        <div class="mt-3">
          <small class="text-muted">
            <i class="pi pi-info-circle mr-1"></i>
            Il file CSV deve contenere uno o più record per <strong>SM1 (Sospensioni)</strong>.
            Il CSV deve essere formattato secondo le specifiche indicate dal SII.
          </small>
        </div>
      </div>

      <!-- Sezione 3: Tabella Richieste Inviate (OK) -->
      <SospensioniRichiesteTable :recordRichieste="recordRichieste" :loading="loadingRichieste"
        @refresh="caricaRecordRichieste" />

      <!-- Sezione 4: Tabella record in KO -->
      <SospensioniKOTable :recordKO="recordKO" :loading="loadingKO" @refresh="caricaRecordKO"
        @revisiona="apriModalRevisione" @valida="validaKO" />
    </div>

    <!-- Dialog Revisione e Reinvio -->
    <Dialog v-model:visible="dialogRevisione" modal header="Revisiona e Reinvia - Sospensione"
      :style="{ width: '800px' }" :closable="true">
      <div v-if="recordInRevisione" class="p-fluid">
        <!-- Messaggio di errore dettagliato -->
        <div v-if="recordInRevisione.motivazione" class="error-message mb-4">
          <div class="flex align-items-start gap-3">
            <i class="pi pi-exclamation-triangle" style="font-size: 1.5rem; color: #f44336;"></i>
            <div style="flex: 1;">
              <h6 class="m-0 mb-2" style="color: #f44336;">Errore rilevato dalla PDC</h6>
              <p class="m-0" style="font-size: 0.9rem; line-height: 1.5;">
                <strong>Causale:</strong> {{ recordInRevisione.cod_causale || 'N/D' }}<br>
                <strong>Motivazione:</strong> {{ recordInRevisione.motivazione }}
              </p>
            </div>
          </div>
        </div>

        <SospensioniForm v-model="datiRevisione" :errorFields="errorFields" />

        <div class="flex justify-content-end mt-4 gap-2">
          <Button label="Annulla" icon="pi pi-times" class="p-button-text" @click="dialogRevisione = false" />
          <Button label="Reinvia alla PDC" icon="pi pi-send" class="p-button-warning" @click="reinviaRecordRevisionato"
            :loading="loadingReinvio" />
        </div>
      </div>
    </Dialog>
  </div>
</template>

<script>
import { ref, onMounted } from "vue";
import Service from "@/services/ask-pratiche/invio-pdc";
import { useToast } from 'primevue/usetoast';
import SospensioniForm from '../forms/SospensioniForm.vue';
import SospensioniKOTable from '../ko-tables/SospensioniKOTable.vue';
import SospensioniRichiesteTable from '../richieste-tables/SospensioniRichiesteTable.vue';

export default {
  name: 'SospensioniPage',
  components: {
    SospensioniForm,
    SospensioniKOTable,
    SospensioniRichiesteTable
  },
  setup() {
    const toast = useToast();
    const service = new Service();

    const formData = ref({});
    const formManualeAperto = ref(false);
    const recordKO = ref([]);
    const recordRichieste = ref([]);
    const loadingKO = ref(false);
    const loadingRichieste = ref(false);
    const loadingRecord = ref(false);
    const dialogRevisione = ref(false);
    const recordInRevisione = ref(null);
    const datiRevisione = ref({});
    const loadingReinvio = ref(false);
    const errorFields = ref([]);

    const handleFileUpload = async (event) => {
      try {
        const file = event.files[0];
        
        // Validazione file
        if (!file) {
          toast.add({
            severity: 'warn',
            summary: 'File Mancante',
            detail: 'Seleziona un file CSV prima di procedere',
            life: 4000
          });
          return;
        }

        // Validazione estensione
        const extension = file.name.split('.').pop().toLowerCase();
        if (extension !== 'csv') {
          toast.add({
            severity: 'error',
            summary: 'Formato File Non Valido',
            detail: `Formato file non supportato: ${extension}. È richiesto un file CSV.`,
            life: 5000
          });
          return;
        }

        // Validazione dimensione (max 5MB)
        if (file.size > 5000000) {
          toast.add({
            severity: 'error',
            summary: 'File Troppo Grande',
            detail: 'Il file CSV supera la dimensione massima consentita di 5MB',
            life: 5000
          });
          return;
        }

        const response = await service.uploadCSVPDC(file, 'sospensioni');

        toast.add({
          severity: 'success',
          summary: 'File inviato',
          detail: response.message || 'File CSV inviato con successo alla PDC per Sospensioni',
          life: 3000
        });

        await caricaRecordKO();
        await caricaRecordRichieste();

      } catch (error) {
        console.error('Errore invio CSV:', error);
        
        // Gestione errori specifici dal controller
        const errorData = error.response?.data;
        
        if (errorData) {
          // Errore 400: Validazione input
          if (error.response.status === 400) {
            toast.add({
              severity: 'error',
              summary: 'Dati Non Validi',
              detail: errorData.message || 'I dati forniti non sono validi',
              life: 5000
            });
          }
          // Errore 422: Validazione CSV o POD con stato non valido
          else if (error.response.status === 422) {
            if (errorData.errors?.invalid_pods) {
              // POD con stato non valido o non trovati
              const invalidPods = errorData.errors.invalid_pods;
              const podsNonTrovati = invalidPods.filter(p => p.stato_attuale === 'NON_TROVATO');
              const podsStatoErrato = invalidPods.filter(p => p.stato_attuale !== 'NON_TROVATO');
              
              let detailMessage = '';
              
              if (podsNonTrovati.length > 0) {
                const listaNonTrovati = podsNonTrovati.map(p => p.pod).join(', ');
                detailMessage += `POD non presenti nel database: ${listaNonTrovati}`;
              }
              
              if (podsStatoErrato.length > 0) {
                if (detailMessage) detailMessage += '\n\n';
                const listaStatoErrato = podsStatoErrato.map(p => p.pod).join(', ');
                detailMessage += `Non è possibile inviare la pratica per i seguenti POD che non sono nello stato richiesto: ${listaStatoErrato}`;
              }
              
              toast.add({
                severity: 'error',
                summary: 'Errore Validazione POD',
                detail: detailMessage,
                life: 10000
              });
            } else if (errorData.errors) {
              // Errori di validazione CSV
              const erroriCSV = Array.isArray(errorData.errors) 
                ? errorData.errors.join(', ') 
                : JSON.stringify(errorData.errors);
              
              toast.add({
                severity: 'error',
                summary: 'Errore Validazione CSV',
                detail: `${errorData.message}: ${erroriCSV}`,
                life: 8000
              });
            } else {
              toast.add({
                severity: 'error',
                summary: 'Errore Validazione',
                detail: errorData.message || 'Errore durante la validazione del CSV',
                life: 5000
              });
            }
          }
          // Errore 500: Errore server
          else if (error.response.status === 500) {
            toast.add({
              severity: 'error',
              summary: 'Errore del Server',
              detail: errorData.message || 'Errore interno del server durante l\'elaborazione',
              life: 5000
            });
          }
          // Altri errori
          else {
            toast.add({
              severity: 'error',
              summary: 'Errore',
              detail: errorData.message || 'Errore durante l\'invio del file CSV',
              life: 5000
            });
          }
        } else {
          // Errore generico di rete
          toast.add({
            severity: 'error',
            summary: 'Errore di Connessione',
            detail: 'Impossibile comunicare con il server. Verifica la connessione.',
            life: 5000
          });
        }
      }
    };

    const inviaRecordSingolo = async () => {
      loadingRecord.value = true;
      try {
        // Validazione campi obbligatori (CP_UTENTE è opzionale, verrà generato dal backend se vuoto)
        const campiObbligatori = [
          'cod_pod', 
          'piva_utente', 
          'piva_gestore', 
          'cod_prestazione', 
          'cod_contr_disp', 
          'importo_morosita'
        ];

        // CF e P.IVA cliente: almeno uno deve essere presente
        const hasCForPIVA = formData.value.cf || formData.value.piva || formData.value.cf_straniero;

        // Valida campi obbligatori base
        const campiMancanti = campiObbligatori.filter(campo => !formData.value[campo] && formData.value[campo] !== 0);
        
        if (campiMancanti.length > 0) {
          const nomiCampi = {
            cod_pod: 'POD',
            piva_utente: 'P.IVA Utente',
            piva_gestore: 'P.IVA Gestore',
            cod_prestazione: 'Codice Prestazione',
            cod_contr_disp: 'Codice Controparte Dispacciamento',
            importo_morosita: 'Importo Morosità'
          };
          
          const elencoMancanti = campiMancanti.map(c => nomiCampi[c] || c).join(', ');
          
          toast.add({
            severity: 'warn',
            summary: 'Campi Obbligatori Mancanti',
            detail: `Compila i seguenti campi: ${elencoMancanti}`,
            life: 5000
          });
          return;
        }

        // Valida CF o P.IVA cliente
        if (!hasCForPIVA) {
          toast.add({
            severity: 'warn',
            summary: 'Identificativo Cliente Richiesto',
            detail: 'Inserisci almeno uno tra Codice Fiscale, P.IVA o CF Straniero del cliente',
            life: 4000
          });
          return;
        }

        // Prepara il payload rimuovendo CP_UTENTE se vuoto (verrà generato dal backend)
        const payload = { ...formData.value };
        if (!payload.cp_utente || payload.cp_utente.trim() === '') {
          delete payload.cp_utente;
        }

        const response = await service.inviaRecordPDC('sospensioni', payload);

        toast.add({
          severity: 'success',
          summary: 'Record Inviato',
          detail: response.message || 'Record inviato con successo alla PDC',
          life: 3000
        });

        formData.value = {};
        await caricaRecordKO();
        await caricaRecordRichieste();

      } catch (error) {
        console.error('Errore invio record:', error);
        
        const errorData = error.response?.data;
        
        if (errorData) {
          // Errore 400: Campi mancanti o dati non validi
          if (error.response.status === 400) {
            toast.add({
              severity: 'error',
              summary: 'Dati Non Validi',
              detail: errorData.message || 'Verifica i dati inseriti e riprova',
              life: 5000
            });
          }
          // Errore 422: POD con stato non valido o non trovato
          else if (error.response.status === 422) {
            if (errorData.data?.cod_pod) {
              const codPod = errorData.data.cod_pod;
              const statoAttuale = errorData.data.stato_attuale;
              
              if (statoAttuale === 'NON_TROVATO') {
                toast.add({
                  severity: 'error',
                  summary: 'POD Non Trovato',
                  detail: `Il POD ${codPod} non esiste nel database`,
                  life: 7000
                });
              } else {
                toast.add({
                  severity: 'error',
                  summary: 'Stato POD Non Valido',
                  detail: `Non è possibile inviare la pratica per il POD ${codPod} che non è nello stato richiesto`,
                  life: 7000
                });
              }
            } else {
              toast.add({
                severity: 'error',
                summary: 'Validazione Fallita',
                detail: errorData.message || 'I dati forniti non hanno superato la validazione',
                life: 5000
              });
            }
          }
          // Errore 500: Errore server
          else if (error.response.status === 500) {
            toast.add({
              severity: 'error',
              summary: 'Errore del Server',
              detail: errorData.message || 'Errore interno del server durante l\'elaborazione',
              life: 5000
            });
          }
          // Altri errori
          else {
            toast.add({
              severity: 'error',
              summary: 'Errore',
              detail: errorData.message || error.message || 'Errore durante l\'invio del record',
              life: 5000
            });
          }
        } else {
          // Errore generico
          toast.add({
            severity: 'error',
            summary: 'Errore di Connessione',
            detail: 'Impossibile comunicare con il server. Verifica la connessione.',
            life: 5000
          });
        }
      } finally {
        loadingRecord.value = false;
      }
    };

    const caricaRecordKO = async () => {
      loadingKO.value = true;
      try {
        const response = await service.getRecordKOPDC('sospensioni');
        recordKO.value = response || [];

      } catch (error) {
        console.error('Errore caricamento KO:', error);
        toast.add({
          severity: 'error',
          summary: 'Errore',
          detail: 'Errore durante il caricamento dei record KO',
          life: 3000
        });
      } finally {
        loadingKO.value = false;
      }
    };

    const caricaRecordRichieste = async () => {
      loadingRichieste.value = true;
      try {
        const response = await service.getRecordOKPDC('sospensioni');
        recordRichieste.value = response || [];
      } catch (error) {
        console.error('Errore caricamento richieste:', error);
        toast.add({
          severity: 'error',
          summary: 'Errore',
          detail: 'Errore durante il caricamento delle richieste',
          life: 3000
        });
      } finally {
        loadingRichieste.value = false;
      }
    };

    const apriModalRevisione = async (record) => {
      recordInRevisione.value = record;
      loadingReinvio.value = true;

      try {
        // Identifica i campi in errore dalla motivazione
        errorFields.value = [];
        if (record.motivazione) {
          errorFields.value.push('motivazione');

          // Analizza la motivazione per capire quale campo è in errore
          const motivazioneLower = record.motivazione.toLowerCase();

          if (motivazioneLower.includes('pod')) {
            errorFields.value.push('cod_pod');
          }
          if (motivazioneLower.includes('pratica')) {
            errorFields.value.push('cp_utente');
          }
          if (motivazioneLower.includes('contratto') || motivazioneLower.includes('dispacciamento')) {
            errorFields.value.push('cod_contr_disp');
          }
        }

        // CHIAMATA API: Recupera la richiesta originale tramite CP_UTENTE e COD_CAUSALE
        const richiestaOriginale = await service.getRichiestaOriginalePDC(
          'sospensioni',
          record.cp_utente, 
        );

        // Popola il form con i dati della richiesta originale
        datiRevisione.value = { ...richiestaOriginale };

        dialogRevisione.value = true;

      } catch (error) {
        console.error('Errore recupero richiesta originale:', error);
        toast.add({
          severity: 'error',
          summary: 'Errore',
          detail: 'Errore durante il recupero dei dati della richiesta originale',
          life: 5000
        });
      } finally {
        loadingReinvio.value = false;
      }
    };

    const reinviaRecordRevisionato = async () => {
      loadingReinvio.value = true;
      try {
        // Validazione campi obbligatori
        // Nota: cp_utente è opzionale e verrà generato automaticamente se vuoto
        // Nota: importo_morosita può essere 0 (valore valido)
        const campiObbligatori = [
          'cod_pod', 
          'piva_utente', 
          'piva_gestore', 
          'cod_prestazione', 
          'cod_contr_disp'
        ];

        // CF e P.IVA cliente: almeno uno deve essere presente
        const hasCForPIVA = datiRevisione.value.cf || datiRevisione.value.piva || datiRevisione.value.cf_straniero;

        // Valida campi obbligatori base
        const campiMancanti = campiObbligatori.filter(campo => !datiRevisione.value[campo]);
        
        // Validazione specifica per importo_morosita: deve esistere (può essere 0)
        if (datiRevisione.value.importo_morosita === null || datiRevisione.value.importo_morosita === undefined) {
          campiMancanti.push('importo_morosita');
        }
        
        if (campiMancanti.length > 0) {
          const nomiCampi = {
            cod_pod: 'POD',
            piva_utente: 'P.IVA Utente',
            piva_gestore: 'P.IVA Gestore',
            cod_prestazione: 'Codice Prestazione',
            cod_contr_disp: 'Codice Controparte Dispacciamento',
            importo_morosita: 'Importo Morosità'
          };
          
          const elencoMancanti = campiMancanti.map(c => nomiCampi[c] || c).join(', ');
          
          toast.add({
            severity: 'warn',
            summary: 'Campi Obbligatori Mancanti',
            detail: `Compila i seguenti campi prima di reinviare: ${elencoMancanti}`,
            life: 5000
          });
          return;
        }

        // Valida CF o P.IVA cliente
        if (!hasCForPIVA) {
          toast.add({
            severity: 'warn',
            summary: 'Identificativo Cliente Richiesto',
            detail: 'Inserisci almeno uno tra Codice Fiscale, P.IVA o CF Straniero del cliente',
            life: 4000
          });
          return;
        }

        const response = await service.inviaRecordPDC('sospensioni', datiRevisione.value);

        toast.add({
          severity: 'success',
          summary: 'Reinvio completato',
          detail: response.message || `Record ${datiRevisione.value.cp_utente} reinviato con successo`,
          life: 3000
        });

        dialogRevisione.value = false;
        recordInRevisione.value = null;
        datiRevisione.value = {};
        errorFields.value = [];

        await caricaRecordKO();
        await caricaRecordRichieste();

      } catch (error) {
        console.error('Errore reinvio:', error);
        
        const errorData = error.response?.data;
        
        if (errorData) {
          // Errore 400: Campi mancanti o dati non validi
          if (error.response.status === 400) {
            toast.add({
              severity: 'error',
              summary: 'Dati Non Validi',
              detail: errorData.message || 'Verifica i dati inseriti e riprova',
              life: 5000
            });
          }
          // Errore 422: POD con stato non valido, non trovato o validazione fallita
          else if (error.response.status === 422) {
            if (errorData.data?.cod_pod) {
              const codPod = errorData.data.cod_pod;
              const statoAttuale = errorData.data.stato_attuale;
              
              if (statoAttuale === 'NON_TROVATO') {
                toast.add({
                  severity: 'error',
                  summary: 'POD Non Trovato',
                  detail: `Il POD ${codPod} non esiste nel database`,
                  life: 7000
                });
              } else {
                toast.add({
                  severity: 'error',
                  summary: 'Stato POD Non Valido',
                  detail: `Non è possibile inviare la pratica per il POD ${codPod} che non è nello stato richiesto`,
                  life: 7000
                });
              }
            } else {
              toast.add({
                severity: 'error',
                summary: 'Validazione Fallita',
                detail: errorData.message || 'I dati forniti non hanno superato la validazione',
                life: 5000
              });
            }
          }
          // Errore 500: Errore server
          else if (error.response.status === 500) {
            toast.add({
              severity: 'error',
              summary: 'Errore del Server',
              detail: errorData.message || 'Errore interno del server durante l\'elaborazione',
              life: 5000
            });
          }
          // Altri errori
          else {
            toast.add({
              severity: 'error',
              summary: 'Errore Reinvio',
              detail: errorData.message || 'Errore durante il reinvio del record',
              life: 5000
            });
          }
        } else {
          // Errore generico di rete
          toast.add({
            severity: 'error',
            summary: 'Errore di Connessione',
            detail: 'Impossibile comunicare con il server. Verifica la connessione.',
            life: 5000
          });
        }
      } finally {
        loadingReinvio.value = false;
      }
    };

    const validaKO = async (record) => {
      // Conferma validazione KO
      if (!confirm(`Vuoi validare il KO per il POD ${record.cod_pod || record.pod}?\n\nQuesto cambierà lo stato del POD da KO temporaneo a KO definitivo.`)) {
        return;
      }

      try {
        loadingKO.value = true;

        const codPod = record.cod_pod || record.pod;
        const result = await service.validateKoRecord('sospensioni', codPod);

        if (result.success) {
          toast.add({
            severity: 'success',
            summary: 'KO Validato',
            detail: `POD ${codPod} validato con successo. Stato cambiato a KO definitivo.`,
            life: 3000
          });

          // Ricarica la lista KO per rimuovere il record validato
          await caricaRecordKO();
        } else {
          toast.add({
            severity: 'error',
            summary: 'Errore',
            detail: result.message || 'Errore durante la validazione del KO',
            life: 5000
          });
        }
      } catch (error) {
        console.error('Errore durante la validazione KO:', error);
        
        const errorMessage = error.response?.data?.message || error.message || 'Errore durante la validazione del KO';
        
        toast.add({
          severity: 'error',
          summary: 'Errore',
          detail: errorMessage,
          life: 5000
        });
      } finally {
        loadingKO.value = false;
      }
    };

    onMounted(() => {
      caricaRecordKO();
      caricaRecordRichieste();
    });

    return {
      formData,
      formManualeAperto,
      recordKO,
      recordRichieste,
      loadingKO,
      loadingRichieste,
      loadingRecord,
      dialogRevisione,
      recordInRevisione,
      datiRevisione,
      loadingReinvio,
      errorFields,
      handleFileUpload,
      inviaRecordSingolo,
      caricaRecordKO,
      caricaRecordRichieste,
      apriModalRevisione,
      reinviaRecordRevisionato,
      validaKO
    };
  }
};
</script>

<style scoped>
.sospensioni-page {
  padding: 1rem;
}

.card {
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  transition: box-shadow 0.3s ease;
}

.card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
}

/* Form Card Styling */
.form-card {
  background: linear-gradient(135deg, #fff9e6 0%, #fffbf0 100%);
  border: 1px solid #ffe082;
  position: relative;
  z-index: 100;
}

.form-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  cursor: pointer;
  user-select: none;
  transition: all 0.2s ease;
}

.form-header:hover {
  background-color: rgba(255, 193, 7, 0.1);
  border-radius: 12px;
}

.form-header:hover h5 {
  color: #F57C00;
}

.form-content {
  padding: 0 1rem 1rem 1rem;
}

/* Error Message in Dialog */
.error-message {
  background: linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%);
  border: 1px solid #ef5350;
  border-left: 4px solid #f44336;
  border-radius: 8px;
  padding: 1rem;
}

.error-message h6 {
  font-weight: 700;
}

.error-message p {
  color: #333;
}

/* Animations */
.expand-enter-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
  overflow: hidden;
}

.expand-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
  overflow: hidden;
}

.expand-enter-from {
  opacity: 0;
  transform: translateY(-10px);
}

.expand-enter-to {
  opacity: 1;
  transform: translateY(0);
}

.expand-leave-from {
  opacity: 1;
  transform: translateY(0);
}

.expand-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

/* Utility Classes */
.text-muted {
  color: #6c757d;
}

.cursor-pointer {
  cursor: pointer;
  user-select: none;
}

/* PrimeVue Overrides */
:deep(.p-fileupload) {
  border: 2px dashed #2196F3;
  border-radius: 8px;
  transition: border-color 0.3s ease;
}

:deep(.p-fileupload:hover) {
  border-color: #1976D2;
}
</style>
